/**
 * VolunteerBenefit — /volunteer/benefits/:slug
 *
 * Long-form article page for each of the 9 Volunteer Impact tabs.
 * Content is sourced from src/data/volunteerBenefits.js.
 *
 * Layout:
 *   1. Wide colour-tinted hero band with slug-specific gradient overlay,
 *      big emoji, tagline and headline.
 *   2. Article intro paragraphs on a cream card.
 *   3. Numbered benefits list with per-item icon + colour block.
 *   4. Closing paragraph in a subtle accent card.
 *   5. Cross-nav: "← All benefits" + "Next: {title} →".
 *   6. CTA row: Find opportunities · Explore organizations.
 */
import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Calendar, Building2, Sparkles, Quote } from "lucide-react";
import { VOLUNTEER_BENEFITS, VOLUNTEER_BENEFITS_BY_SLUG } from "@/data/volunteerBenefits";
import BenefitShareCard from "@/components/BenefitShareCard";

export default function VolunteerBenefit() {
  const { slug } = useParams();
  const article = VOLUNTEER_BENEFITS_BY_SLUG[slug];

  const { prev, next } = useMemo(() => {
    const idx = VOLUNTEER_BENEFITS.findIndex((b) => b.slug === slug);
    return {
      prev: idx > 0 ? VOLUNTEER_BENEFITS[idx - 1] : null,
      next: idx >= 0 && idx < VOLUNTEER_BENEFITS.length - 1
              ? VOLUNTEER_BENEFITS[idx + 1] : null,
    };
  }, [slug]);

  if (!article) {
    return (
      <div className="max-w-3xl mx-auto py-24 text-center px-6" data-testid="volunteer-benefit-not-found">
        <h1 className="font-display text-3xl">This article was not found</h1>
        <p className="mt-3 opacity-70">The benefit you're looking for may have moved.</p>
        <Link to="/volunteer" className="wofy-btn-primary mt-6 inline-flex">
          <ArrowLeft className="w-4 h-4" /> Back to Volunteer home
        </Link>
      </div>
    );
  }

  const c = article.color;

  return (
    <div data-testid={`volunteer-benefit-page-${article.slug}`} className="pb-20">
      {/* ── HERO ─────────────────────────────────────────────── */}
      <section
        className="relative overflow-hidden"
        data-testid="volunteer-benefit-hero"
      >
        <img
          src={article.hero}
          alt={article.title}
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
        />
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(120deg, ${c}dd 0%, ${c}55 60%, rgba(15,23,42,0.55) 100%)`,
          }}
        />
        <div className="relative z-10 max-w-[1100px] mx-auto px-6 lg:px-10 py-20 sm:py-28 text-white">
          <Link
            to="/volunteer"
            className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-widest opacity-90 hover:opacity-100"
            data-testid="volunteer-benefit-back"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> All Volunteer benefits
          </Link>
          <div className="mt-6 flex items-center gap-3">
            <span
              className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-white/15 backdrop-blur border border-white/20 text-3xl"
              aria-hidden
            >
              {article.emoji}
            </span>
            <div>
              <div className="overline text-white/80">SOCIAL PULSE · VOLUNTEER IMPACT</div>
              <div className="font-mono text-sm opacity-90">{article.title}</div>
            </div>
          </div>
          <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight max-w-4xl">
            {article.headline}
          </h1>
          <p className="mt-4 max-w-2xl text-white/95 text-lg leading-relaxed">
            {article.tagline}
          </p>
        </div>
      </section>

      {/* ── ARTICLE BODY ────────────────────────────────────── */}
      <section className="max-w-[900px] mx-auto px-6 lg:px-10 -mt-10 relative z-20">
        <article
          className="rounded-3xl border p-8 sm:p-10"
          style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-bg)" }}
        >
          <div className="space-y-5 text-base leading-relaxed" style={{ color: "var(--wofy-text)" }}>
            {article.intro.map((p, i) => (
              <p key={i} data-testid={`volunteer-benefit-intro-${i}`}>{p}</p>
            ))}
          </div>

          {/* Section list */}
          <div className="mt-10">
            <div className="overline mb-3" style={{ color: c }}>
              {article.sectionTitle}
            </div>
            <div className="space-y-4">
              {article.sections.map((s, i) => (
                <div
                  key={s.title}
                  className="flex gap-4 rounded-2xl border p-4 sm:p-5"
                  style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-surface)" }}
                  data-testid={`volunteer-benefit-section-${i}`}
                >
                  <div
                    className="shrink-0 w-11 h-11 rounded-xl flex items-center justify-center text-2xl"
                    style={{ background: `${c}18`, color: c }}
                    aria-hidden
                  >
                    {s.icon}
                  </div>
                  <div>
                    <div className="font-display text-lg">{s.title}</div>
                    <p className="mt-1 text-sm leading-relaxed" style={{ color: "var(--wofy-text-soft)" }}>
                      {s.body}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Closing */}
          <div
            className="mt-10 rounded-2xl border-l-4 pl-5 py-3"
            style={{ borderColor: c, background: `${c}0d`, color: "var(--wofy-text)" }}
            data-testid="volunteer-benefit-closing"
          >
            <div className="flex items-center gap-2 mb-1" style={{ color: c }}>
              <Sparkles className="w-4 h-4" />
              <span className="overline">Small acts, big change</span>
            </div>
            <p className="text-base leading-relaxed">{article.closing}</p>
          </div>

          {/* Testimonial */}
          {article.testimonial && (
            <figure
              className="mt-8 rounded-2xl border overflow-hidden"
              style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-surface)" }}
              data-testid="volunteer-benefit-testimonial"
            >
              <div className="p-5 sm:p-6 flex gap-4 items-start">
                <Quote
                  className="shrink-0 w-8 h-8 opacity-70 rotate-180"
                  style={{ color: c }}
                  aria-hidden
                />
                <div className="flex-1">
                  <blockquote className="font-display text-lg leading-relaxed" style={{ color: "var(--wofy-text)" }}>
                    “{article.testimonial.quote}”
                  </blockquote>
                  <figcaption className="mt-4 flex items-center gap-3">
                    {article.testimonial.avatar && (
                      <img
                        src={article.testimonial.avatar}
                        alt=""
                        className="w-10 h-10 rounded-full object-cover border"
                        style={{ borderColor: `${c}55` }}
                      />
                    )}
                    <div>
                      <div className="font-medium text-sm">{article.testimonial.name}</div>
                      <div className="text-xs opacity-70">{article.testimonial.role}</div>
                    </div>
                  </figcaption>
                </div>
              </div>
              <div className="h-1" style={{ background: c }} aria-hidden />
            </figure>
          )}

          {/* CTAs */}
          <div className="mt-8 flex flex-wrap gap-3" data-testid="volunteer-benefit-ctas">
            <Link to="/opportunities" className="wofy-btn-primary">
              <Calendar className="w-4 h-4" /> Find opportunities
            </Link>
            <Link to="/organizations" className="wofy-btn-ghost">
              <Building2 className="w-4 h-4" /> Explore organizations
            </Link>
            <Link to="/submit/cause" className="wofy-btn-ghost text-sm">
              Publish a request →
            </Link>
          </div>
        </article>

        {/* Share card */}
        <BenefitShareCard article={article} />

        {/* Prev / Next nav */}
        <nav
          className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3"
          data-testid="volunteer-benefit-nav"
        >
          {prev ? (
            <Link
              to={`/volunteer/benefits/${prev.slug}`}
              className="group rounded-2xl border p-4 hover:-translate-y-0.5 transition-transform"
              style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-surface)" }}
              data-testid="volunteer-benefit-prev"
            >
              <div className="overline flex items-center gap-2" style={{ color: prev.color }}>
                <ArrowLeft className="w-3.5 h-3.5" /> Previous
              </div>
              <div className="mt-1 font-display text-lg">{prev.emoji} {prev.title}</div>
              <div className="text-xs opacity-70 mt-1">{prev.tagline}</div>
            </Link>
          ) : <div />}

          {next ? (
            <Link
              to={`/volunteer/benefits/${next.slug}`}
              className="group rounded-2xl border p-4 hover:-translate-y-0.5 transition-transform text-right"
              style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-surface)" }}
              data-testid="volunteer-benefit-next"
            >
              <div className="overline flex items-center justify-end gap-2" style={{ color: next.color }}>
                Next <ArrowRight className="w-3.5 h-3.5" />
              </div>
              <div className="mt-1 font-display text-lg">{next.title} {next.emoji}</div>
              <div className="text-xs opacity-70 mt-1">{next.tagline}</div>
            </Link>
          ) : <div />}
        </nav>
      </section>
    </div>
  );
}
